import React from 'react';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import {
    compose,
    branch,
    renderComponent,
    lifecycle,
    withProps,
    setDisplayName,
} from 'react-recompose';

import withReduxConnect from './withReduxConnect.js';
import omitProps from './omitProps.js';

import actions from '~/state/ducks/ui/actions.js';
import { dataLoading } from '~/state/ducks/ui/selectors.js';
import { asyncDataLoading } from '~/state/ducks/ui/operations.js';

import { Icon, Header } from 'semantic-ui-react';


const { getData, isLoading, isLoadingFail } = dataLoading;

const myProps = [
    'name',
    'operation',
    'selector',
    'dataName',
    'hasSelector',
    'handleDataLoading',
    'handleDataDestroy',
];


const Container = styled.div`
    display: flex;
    align-items: center;
    height: 400px;
`;


const Spinner = () => (
    <Container>
        <Helmet title="Loading" />
        <Header as="h4" icon textAlign="center">
            <Icon loading name="spinner" />
        </Header>
    </Container>
);

const LoadingFailed = () => (
    <Container>
        <Header as="h2" icon textAlign="center">
            <Icon name="wrench" />
            <Header.Content>Loading Failed</Header.Content>
        </Header>
    </Container>
);


export const withLoadingBranch = compose(
    setDisplayName('withLoadingBranch'),
    branch(({ isLoading }) => isLoading, renderComponent(Spinner)),
    branch(({ isLoadingFail }) => isLoadingFail, renderComponent(LoadingFailed)),
);

const withReduxDataLoading = ({
    name,
    operation,
    selector = null,
    dataName = 'data',
}) => compose(
    setDisplayName('withReduxDataLoading'),
    withProps({
        name,
        dataName,
        selector,
        hasSelector: Boolean(selector),
    }),
    withReduxConnect({
        mapStateToProps: (state, {
            name,
            dataName,
            selector,
            hasSelector,
        }) => ({
            [dataName]: hasSelector ? selector(state) : getData(name)(state),
            isLoading: isLoading(name)(state),
            isLoadingFail: isLoadingFail(name)(state),
        }),
        mapDispatchToProps: {
            operation,
            handleDataLoading: asyncDataLoading,
            handleDataDestroy: actions.dataLoading.destroy,
        },
    }),
    lifecycle({
        componentDidMount() {
            const {
                handleDataLoading,
                name,
                operation,
                hasSelector,
                ...rest
            } = this.props;

            // filter out enhancer props;
            const ownProps = Object.entries(rest).reduce(
                (acc, [key, value]) => ((!myProps.includes(key))
                    ? ({ ...acc, [key]: value })
                    : acc),
                {},
            );

            handleDataLoading(name, operation, hasSelector, ownProps);
        },

        componentWillUnmount() {
            const { name, handleDataDestroy } = this.props;

            handleDataDestroy(name);
        },
    }),
    omitProps(myProps),
);

export default withReduxDataLoading;
